import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchIbamaConamaReunion } from '@/services/ibama';
import { dispositionLabel } from '@/pages/ibama/judgeLabels';

/** '02020000645200552' -> '02020.000645/2005-52'; anything else passes through. */
function formatNup(digits: string): string {
  return /^\d{17}$/.test(digits)
    ? `${digits.slice(0, 5)}.${digits.slice(5, 11)}/${digits.slice(11, 15)}-${digits.slice(15)}`
    : digits;
}

/**
 * One CER reunion's docket. Each row is one process item as the minutes
 * record it; the relator column links to the person's CONAMA-track profile
 * only where the transcript's speaker marks name them.
 */
export function IbamaConamaReunionPage() {
  const { reunion = '' } = useParams();
  const reunionNumber = Number(reunion);
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'conama', 'reunion', reunionNumber],
    queryFn: () => fetchIbamaConamaReunion(reunionNumber),
    enabled: Number.isFinite(reunionNumber),
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">
          {t('ibama:conama.reunion.title', { n: data.session.reunion_number })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {data.session.held_from ?? '—'}
          {data.session.held_to && data.session.held_to !== data.session.held_from
            ? ` – ${data.session.held_to}`
            : ''}
          {' · '}
          {t('ibama:conama.reunion.counts', {
            items: number.format(data.session.items),
            judged: number.format(data.session.judged),
            named: number.format(data.session.with_relator),
          })}
        </p>
      </header>

      <ExplainerCard i18nKey="ibama:conama.reunion.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-items>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.process')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.autuado')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.relatoria')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.disposition')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.judgedOn')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.reunion.relator')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i} className="border-t" data-item={it.process_number}>
                  <td className="px-3 py-2 text-foreground">
                    {it.process_url ? (
                      <a
                        href={it.process_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {formatNup(it.process_number)}
                      </a>
                    ) : (
                      formatNup(it.process_number)
                    )}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-xs text-muted-foreground">
                    {it.autuado ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {it.relatoria ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {it.disposition ? dispositionLabel(t, it.disposition) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {it.judged_on ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {it.relator_slug ? (
                      <Link
                        to={`/ibama/conama/r/${it.relator_slug}`}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {it.relator_name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs">
          <a
            href={data.session.page_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-2 hover:underline"
            data-source-link
          >
            {t('ibama:conama.sourceLink')}
          </a>
          <Link to="/ibama/conama/board" className="text-primary underline-offset-2 hover:underline">
            {t('ibama:conama.reunion.backToBoard')}
          </Link>
        </div>
      </section>
    </div>
  );
}
